// Pure helpers for the Market Probabilities one-touch ladder. No React, no WS —
// kept side-effect free so the rung maths is unit-testable in isolation
// (ladder.test.ts).

import type { Instrument, ModelKind, PayType } from '../exotics-pricer/contracts';
import { normalizeExchangeToSource, SourceTypeEnum } from '../../model/source_type.enum';
import { isDeribitExchange } from '../../model/token';

// Resolve a market's candlestick SOURCE from the live vol-surface registry.
// Each market's MTD spot series lives on the venue the engine sources its
// surface from -- BTC/ETH on Deribit, SOL on Deribit (SOL_USDC), HYPE on
// MEXC (HYPE_USDT). The registry entry carries that venue as `source`; we
// match on `baseQuote` (the wire identifier) and normalize the venue string
// to a SourceTypeEnum. A market that resolves in the registry but carries no
// usable source, or that is absent entirely, falls back to DERIBIT (the
// pre-registry status quo) rather than failing the fetch. Pure: no React,
// no WS -- unit-tested in ladder.test.ts.
export function resolveMarketSource(
  entries: ReadonlyArray<{ baseQuote: string; source: string }>,
  baseQuote: string,
): SourceTypeEnum {
  const entry = entries.find((e) => e.baseQuote === baseQuote);
  if (!entry || typeof entry.source !== 'string' || entry.source.trim() === '') {
    return SourceTypeEnum.DERIBIT;
  }
  return normalizeExchangeToSource(entry.source);
}

// Registry-resolved market identity: the wire `baseQuote`, the display
// `ccy` (the quote currency), the candlestick `source` venue, and the
// candlestick `candleSymbol` (the underlying the candle feed expects). The
// board derives all four for a market that carries no hard-coded override, so
// the new tokens (XRP/DOGE/BNB/AVAX) don't need their quote convention guessed
// (*_USDT vs *_USDC) -- a wrong guess would falsely read "No vol surface".
//
// candleSymbol vs baseQuote (IM-1594 bug fix): pricing keys off baseQuote
// (XRP_USDT) on EVERY venue, but the candlestick feed wants the venue's own
// symbol -- base_quote on Deribit, exchange_symbol (XRPUSDT, no underscore) on
// Binance/MEXC. Mirrors getTokenCandlestickSymbol in model/token.ts exactly:
// isDeribit ? base_quote : exchange_symbol. Sending baseQuote as the candle
// underlying for a non-Deribit venue returns empty -> "No MTD data".
export interface ResolvedMarket {
  baseQuote: string;
  ccy: string;
  source: SourceTypeEnum;
  candleSymbol: string;
}

// Binance wants a CLEAN perpetual symbol: underscore stripped, no `-PERPETUAL`
// suffix (DOGE_USDT -> DOGEUSDT). The candle feed takes 'PERPETUAL' as a
// SEPARATE label arg, so the symbol must not carry it. Constructed from
// baseQuote: drop a trailing `-PERPETUAL` and every underscore. Used ONLY on
// the Binance branch, as a fallback when the registry's exchange_symbol is the
// synthesized default rather than a real venue symbol.
function cleanPerpSymbol(baseQuote: string): string {
  return baseQuote.replace(/-PERPETUAL$/i, '').replace(/_/g, '');
}

// True when a Binance registry exchange_symbol is NOT a usable venue candle
// symbol: the synthesized `${baseQuote}-PERPETUAL` default
// (use-available-tokens.ts:98) the list_vol_surface_tokens parser fills in when
// the registry carries no real exchange_symbol, or an empty/underscore-bearing
// value Binance won't accept as a candle underlying. When this holds we
// construct the clean symbol from baseQuote instead of trusting the registry.
// REACHED ONLY ON THE BINANCE BRANCH -- this is a per-symbol fallback check
// within Binance, NOT a cross-venue strip trigger. MEXC never reaches it.
function isSynthesizedExchangeSymbol(exchangeSymbol: string): boolean {
  if (typeof exchangeSymbol !== 'string' || exchangeSymbol.trim() === '') return true;
  if (/-PERPETUAL$/i.test(exchangeSymbol)) return true;
  if (exchangeSymbol.includes('_')) return true;
  return false;
}

// Candlestick underlying for a registry entry. Three-way split keyed off the
// normalized `source` venue -- NOT off the symbol shape (the `_`-in-symbol
// heuristic mis-fired on MEXC and blanked HYPE: IM-1594 regression):
//   - Deribit (BTC, SOL_USDC)              -> baseQuote.
//   - Binance (DOGE, BNB)                  -> a genuine clean exchange_symbol
//     (XRPUSDT) when present, else the constructed clean symbol (baseQuote, `_`
//     stripped, no -PERPETUAL) when the registry value is the synthesized
//     `${baseQuote}-PERPETUAL` default. Binance rejects the underscore/PERPETUAL
//     forms as a candle underlying -> "No MTD data" without this.
//   - Everything else, incl. MEXC (HYPE)  -> exchange_symbol VERBATIM. MEXC
//     legitimately uses the underscore form (HYPE_USDT); stripping it blanks the
//     feed. This is the pre-regression behaviour HYPE needs restored.
// Pure; unit-tested in ladder.test.ts.
export function candleSymbolFor(entry: {
  baseQuote: string;
  exchangeSymbol: string;
  source: string;
}): string {
  if (isDeribitExchange(entry.source)) return entry.baseQuote;
  if (normalizeExchangeToSource(entry.source) === SourceTypeEnum.BINANCE) {
    return isSynthesizedExchangeSymbol(entry.exchangeSymbol)
      ? cleanPerpSymbol(entry.baseQuote)
      : entry.exchangeSymbol;
  }
  return entry.exchangeSymbol;
}

// Preference order when a token has multiple registry entries: a USD-quoted
// surface beats USDC beats USDT; anything else falls to first-seen. Keeps the
// existing four on their known quote (BTC/ETH -> USD) without an override.
const QUOTE_PREFERENCE = ['USD', 'USDC', 'USDT'];

// Resolve a market's wire identity from the live vol-surface registry.
//
// Two modes:
//   - OVERRIDE (existing four): the caller pins baseQuote + ccy explicitly so
//     their wire never regresses. We still resolve the candlestick `source` AND
//     `candleSymbol` from the registry (matching the pinned baseQuote),
//     defaulting DERIBIT / baseQuote. (HYPE on MEXC only worked because MEXC
//     happened to accept the `_` baseQuote form; resolving its candle symbol
//     off the registry removes that latent trap.)
//   - REGISTRY (new four): no override. We match registry entries by
//     `entry.baseCcy === token` and pick one by QUOTE_PREFERENCE (USD > USDC >
//     USDT) then first-seen. baseQuote + ccy (= quoteCcy) + source +
//     candleSymbol ride off the matched entry. No match -> null: the market
//     lands UNCOVERED and the card renders the graceful "No vol surface" state
//     instead of guessing a quote.
//
// Pure: no React, no WS -- unit-tested in ladder.test.ts.
export function resolveMarketBaseQuote(
  entries: ReadonlyArray<{
    baseQuote: string;
    baseCcy: string;
    quoteCcy: string;
    exchangeSymbol: string;
    source: string;
  }>,
  token: string,
  override?: { baseQuote: string; ccy: string },
): ResolvedMarket | null {
  if (override) {
    // Derive the candle symbol from the registry entry matching the pinned
    // baseQuote. No entry (cold cache / pulled surface) -> fall back to
    // baseQuote, which is the Deribit-correct status quo.
    const matched = entries.find((e) => e.baseQuote === override.baseQuote);
    return {
      baseQuote: override.baseQuote,
      ccy: override.ccy,
      source: resolveMarketSource(entries, override.baseQuote),
      candleSymbol: matched ? candleSymbolFor(matched) : override.baseQuote,
    };
  }
  const matches = entries.filter((e) => e.baseCcy === token);
  if (matches.length === 0) return null;
  const ranked = [...matches].sort((a, b) => {
    const ra = QUOTE_PREFERENCE.indexOf(a.quoteCcy);
    const rb = QUOTE_PREFERENCE.indexOf(b.quoteCcy);
    return (ra === -1 ? QUOTE_PREFERENCE.length : ra) - (rb === -1 ? QUOTE_PREFERENCE.length : rb);
  });
  const chosen = ranked[0];
  return {
    baseQuote: chosen.baseQuote,
    ccy: chosen.quoteCcy,
    source: resolveMarketSource(entries, chosen.baseQuote),
    candleSymbol: candleSymbolFor(chosen),
  };
}

// Signed LV-minus-BS touch-probability spread, in PERCENTAGE POINTS. This is
// the smile signal: positive means Local Vol assigns MORE touch probability
// than Black-Scholes (the smile adds), negative means it removes. Both inputs
// are probabilities in [0, 1]; the result is (probLv - probBs) * 100. Returns
// null if either input is missing or non-finite (HIT / error / not-yet-priced
// rungs), so the card renders nothing rather than a bogus annotation. Pure;
// unit-tested in ladder.test.ts.
export function spreadPp(probLv: number | null, probBs: number | null): number | null {
  if (probLv === null || probBs === null) return null;
  if (!Number.isFinite(probLv) || !Number.isFinite(probBs)) return null;
  return (probLv - probBs) * 100;
}

// One rung of the ladder: a price level and the touch direction relative
// to spot. Levels strictly above spot are OneTouchUpper; strictly below
// are OneTouchLower. A level equal to spot is degenerate and is never
// emitted (buildRungs skips it — spot gets its own marker row).
export type TouchDirection = 'upper' | 'lower';

export interface Rung {
  level: number;
  direction: TouchDirection;
}

// Month-end expiry at 08:00:00, formatted the way buildEngineInstrument
// expects a full timestamp (so it is NOT re-padded). "Month-end" = the
// last calendar day of `now`'s month. Computed, never hard-coded.
//
// Local time is used deliberately: the engine pads bare dates with
// " 08:00:00" in the platform's own convention, and the pricer's existing
// expiry strings are local-wall-clock. Returns e.g. "2026-06-30 08:00:00".
export function monthEndExpiry(now: Date = new Date()): string {
  // Day 0 of the *next* month is the last day of this month.
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const yyyy = last.getFullYear();
  const mm = String(last.getMonth() + 1).padStart(2, '0');
  const dd = String(last.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} 08:00:00`;
}

// Parse an engine expiry string ("YYYY-MM-DD HH:MM:SS", or a bare
// "YYYY-MM-DD") into a Date in LOCAL wall-clock. The engine's expiry strings
// are local (monthEndExpiry emits the platform's own convention), so we
// construct via the Date(y, m, d, h, min, s) ctor rather than Date.parse,
// which would read the bare form as UTC and shift it. Returns null on an
// unparseable input so the formatters can fall back to an em-dash. Pure.
function parseEngineExpiry(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(iso ?? '');
  if (!m) return null;
  const [, yyyy, mm, dd, hh, mi, ss] = m;
  const d = new Date(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    Number(hh ?? '0'),
    Number(mi ?? '0'),
    Number(ss ?? '0'),
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

// Parse an engine expiry string as UTC (NOT local wall-clock). The engine/feed
// emit the expiry time-of-day in UTC (Deribit's 08:00 UTC convention), and the
// board displays all times in UTC per the house rule, so the DISPLAY formatters
// below interpret the string via Date.UTC -- distinct from parseEngineExpiry,
// which stays local for the tab-window comparison logic it feeds. Returns null on
// an unparseable input. Pure.
function parseEngineExpiryUtc(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(iso ?? '');
  if (!m) return null;
  const [, yyyy, mm, dd, hh, mi, ss] = m;
  const t = Date.UTC(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    Number(hh ?? '0'),
    Number(mi ?? '0'),
    Number(ss ?? '0'),
  );
  return Number.isNaN(t) ? null : new Date(t);
}

// Format the selected expiry as a compact UTC stamp: "30JUN26 08:00 UTC" -- day,
// UPPERCASE 3-letter month, 2-digit year, HH:MM, and an explicit UTC label so the
// clock time is never read as browser-local. Reuses MONTH_ABBR so the month set
// stays locale-independent (en-GB's "Sept" trap is avoided). Em-dash on an
// unparseable input. Pure; unit-tested in ladder.test.ts.
export function formatExpiryStamp(iso: string): string {
  const d = parseEngineExpiryUtc(iso);
  if (!d) return '—';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTH_ABBR[d.getUTCMonth()].toUpperCase();
  const yy = String(d.getUTCFullYear() % 100).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `${dd}${mon}${yy} ${hh}:${mi} UTC`;
}

// Live countdown from `now` to the selected expiry, minute-resolution:
// "12d 7h 3m". The expiry is interpreted as UTC (parseEngineExpiryUtc) so the
// remaining time is correct regardless of the viewer's zone. Clamps to
// "0d 0h 0m" once the expiry is in the past (never negative). Em-dash on an
// unparseable expiry. Pure (now is passed in, so the ticking lives in the
// caller's shared timer); unit-tested in ladder.test.ts.
export function formatTimeToExpiry(iso: string, now: Date = new Date()): string {
  const d = parseEngineExpiryUtc(iso);
  if (!d) return '—';
  let ms = d.getTime() - now.getTime();
  if (ms <= 0) return '0d 0h 0m';
  const days = Math.floor(ms / 86400000);
  ms -= days * 86400000;
  const hours = Math.floor(ms / 3600000);
  ms -= hours * 3600000;
  const mins = Math.floor(ms / 60000);
  return `${days}d ${hours}h ${mins}m`;
}

// Compact UTC label for an OTC (exchange-family) expiry tab, matching the card
// footer's "31DEC26" convention: DD + UPPERCASE 3-letter month + 2-digit year, no
// spaces, always including the year -- e.g. "05AUG26", "25SEP26", "26JUN27".
// Interpreted in UTC (parseEngineExpiryUtc) per the board's UTC convention so the
// day never shifts across the date line. Em-dash on an unparseable input. Pure;
// unit-tested in ladder.test.ts.
export function formatOtcTabLabel(iso: string): string {
  const d = parseEngineExpiryUtc(iso);
  if (!d) return '—';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTH_ABBR[d.getUTCMonth()].toUpperCase();
  const yy = String(d.getUTCFullYear() % 100).padStart(2, '0');
  return `${dd}${mon}${yy}`;
}

// Trim the OTC (exchange-family) expiry ladder to start at the first WEEKLY,
// dropping the ultra-near daily expiries (0-2 days out) the feed also serves.
// RULE: Deribit weekly/monthly/quarterly option expiries all land on FRIDAYS at
// 08:00 UTC, while the near-dated dailies fall on other weekdays -- so we start at
// the first Friday expiry (getUTCDay() === 5) and drop everything before it.
// FALLBACK (a token whose cadence isn't Friday-based): start at the first expiry
// that begins the >= 6-day (weekly) spacing -- i.e. the first gap-to-next of a
// week or more -- dropping the tighter sub-weekly leading dailies. If neither rule
// finds a boundary the list is returned unchanged (never hide everything). Input
// is assumed chronologically sorted (collectFamilyExpiries). Pure; unit-tested.
export function otcLadderFromFirstWeekly(sortedIsos: string[]): string[] {
  if (sortedIsos.length <= 1) return sortedIsos;
  const parsed = sortedIsos.map((iso) => parseEngineExpiryUtc(iso));

  const firstFriday = parsed.findIndex((d) => d !== null && d.getUTCDay() === 5);
  if (firstFriday !== -1) return sortedIsos.slice(firstFriday);

  // 6 days (not 7): weekly gaps are 7d, but daily strips that skip the weekend
  // gap up to 3d (Fri->Mon), so 6d cleanly separates weekly from sub-weekly.
  const MIN_WEEKLY_GAP_MS = 6 * 24 * 3600 * 1000;
  for (let i = 0; i < parsed.length - 1; i++) {
    const a = parsed[i];
    const b = parsed[i + 1];
    if (a && b && b.getTime() - a.getTime() >= MIN_WEEKLY_GAP_MS) return sortedIsos.slice(i);
  }
  return sortedIsos;
}

// One expiry-tab descriptor: the engine `iso` ("YYYY-MM-DD 08:00:00", the
// pricing tenor), a short `label` for the tab ("Jul 2026"), and `isQuarterly`
// -- true when the expiry month is on the Mar/Jun/Sep/Dec quarterly cycle
// (monthIndex % 3 === 2). The tab strip badges quarterly tabs with a "Q" so
// the monthly-vs-quarterly split reads at a glance.
export interface MonthEndExpiry {
  iso: string;
  label: string;
  isQuarterly: boolean;
}

// Fixed 3-letter month abbreviations for the tab label. Hard-coded rather than
// Intl-derived because en-GB renders September as "Sept" (4 letters), which
// breaks the uniform 3-letter "MMM YYYY" the board uses; this stays locale- and
// Node-version-independent.
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// True when a month (0-indexed) sits on the Mar/Jun/Sep/Dec quarterly cycle.
// The cycle months are indices 2/5/8/11 -- i.e. monthIndex % 3 === 2.
function isQuarterlyMonth(monthIndex: number): boolean {
  return monthIndex % 3 === 2;
}

// Build a { iso, label, isQuarterly } descriptor for the month-end of the
// month that starts at `monthStart` (the 1st at 00:00). Anchoring on the 1st
// keeps month overflow / year-roll honest via the Date ctor and reuses the
// same monthEndExpiry logic the board's default tab anchors to.
function expiryFor(monthStart: Date): MonthEndExpiry {
  return {
    iso: monthEndExpiry(monthStart),
    label: `${MONTH_ABBR[monthStart.getMonth()]} ${monthStart.getFullYear()}`,
    isQuarterly: isQuarterlyMonth(monthStart.getMonth()),
  };
}

// The expiry-tab set: 3 rolling MONTHLIES (current month-end + next two) then
// the next 2 QUARTERLIES (Mar/Jun/Sep/Dec month-ends) that fall STRICTLY AFTER
// the last monthly's month. From a June "now" that's Jun/Jul/Aug + Sep/Dec.
//
// The first entry equals monthEndExpiry(now) exactly, so the default tab stays
// the current month-end and the ladder it prices is unchanged. The quarterlies
// are sought from the month AFTER the last monthly, so the front month -- even
// when it is itself a quarterly (e.g. June) -- never duplicates into the
// quarterly slot. Year-roll (Dec -> Mar next year) falls out of the Date ctor
// when the quarterly month index overflows past December. Pure; unit-tested in
// ladder.test.ts.
export function boardExpiries(now: Date = new Date()): MonthEndExpiry[] {
  const MONTHLY_COUNT = 3;
  const QUARTERLY_COUNT = 2;

  // If the current month's expiry has already passed, shift the entire window
  // forward by one month so index 0 is always a live (unexpired) contract.
  const curExpiry = parseEngineExpiry(monthEndExpiry(now));
  const baseMonth = curExpiry !== null && curExpiry.getTime() <= now.getTime() ? 1 : 0;

  const out: MonthEndExpiry[] = [];
  for (let i = 0; i < MONTHLY_COUNT; i++) {
    out.push(expiryFor(new Date(now.getFullYear(), now.getMonth() + baseMonth + i, 1)));
  }

  // Walk forward from the month after the last monthly, collecting the next
  // QUARTERLY_COUNT quarterly months. Strictly-after guarantees no overlap with
  // the monthly window, so no de-dup is needed.
  let monthOffset = baseMonth + MONTHLY_COUNT;
  let found = 0;
  while (found < QUARTERLY_COUNT) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    if (isQuarterlyMonth(monthStart.getMonth())) {
      out.push(expiryFor(monthStart));
      found += 1;
    }
    monthOffset += 1;
  }
  return out;
}

// Start of the current month at 00:00:00 LOCAL wall-clock, minute-aligned.
// This is the anchor for the month's FIXED ladder and the dt_1 of the MTD
// spot series. The OT contracts (and Kalshi's monthly markets) are defined
// at month-open, so the rungs are pinned here for the whole month -- they do
// NOT recentre to live spot. (A misaligned begin makes the candlestick /
// timeseries call return an empty result silently, hence the explicit
// 00:00:00 with zeroed seconds/ms.)
export function startOfMonth(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
}

export function startOfYear(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
}

// Kalshi's BTC monthly increment (confirmed by William). The BTC ladder
// rungs are pinned to multiples of this from the start-of-month anchor.
export const BTC_STEP = 2500;

// Number of fractional digits to render for a market whose ladder STEP is
// `step`. The card renders every price for a market at this fixed precision so
// the levels (and the OHLC / Latest tiles and the spot band) read with
// consistent decimals rather than ragged (AVAX step 0.5 -> 1dp: 10.5/10.0/9.5;
// BTC step 2500 -> 0dp; a 0.05 step -> 2dp). Derived from the step's own
// fractional part: integer steps -> 0, else the count of significant fractional
// digits (trailing zeros stripped). Capped at 8 dp as a sane floating-point
// guard. A non-finite / non-positive step falls back to 0. Pure -- unit-tested
// in ladder.test.ts.
export function decimalsForStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  if (Number.isInteger(step)) return 0;
  // toFixed(8) renders the fractional digits without exponent notation; we then
  // strip trailing zeros and count what remains. This avoids the float-noise a
  // raw `step.toString()` can carry (e.g. 0.1 -> "0.1", not "0.1000000004").
  const frac = step.toFixed(8).split('.')[1] ?? '';
  const trimmed = frac.replace(/0+$/, '');
  return trimmed.length;
}

// Rational approximation to the inverse standard-normal CDF (percent-point
// function), Acklam's algorithm, accurate to ~1e-9. Returns ±Infinity at the
// boundary. Used only by rungCountForDelta below; not part of the public API.
function normInv(p: number): number {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  const A = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02,
              1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
  const B = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02,
              6.680131188771972e+01, -1.328068155288572e+01];
  const C = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00,
             -2.549732539343734e+00,  4.374664141464968e+00,  2.938163982698783e+00];
  const D = [ 7.784695709041462e-03,  3.224671290700398e-01,  2.445134137142996e+00,
              3.754408661907416e+00];
  const pLow = 0.02425;
  if (p < pLow) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (((((C[0]*q+C[1])*q+C[2])*q+C[3])*q+C[4])*q+C[5]) /
           ((((D[0]*q+D[1])*q+D[2])*q+D[3])*q+1);
  }
  if (p <= 1 - pLow) {
    const q = p - 0.5;
    const r = q * q;
    return (((((A[0]*r+A[1])*r+A[2])*r+A[3])*r+A[4])*r+A[5])*q /
           (((((B[0]*r+B[1])*r+B[2])*r+B[3])*r+B[4])*r+1);
  }
  const q = Math.sqrt(-2 * Math.log(1 - p));
  return -(((((C[0]*q+C[1])*q+C[2])*q+C[3])*q+C[4])*q+C[5]) /
           ((((D[0]*q+D[1])*q+D[2])*q+D[3])*q+1);
}

// Count of fixed-step rungs per side needed to reach the delta-implied outer
// boundary. The boundary is the BS d₁ put-delta level from the start-of-period
// anchor (r=0): lower = S × exp(N⁻¹(δ) × σ√T + σ²T/2). For δ < 0.5 N⁻¹(δ) < 0
// so the boundary is below S. The upside is symmetric. Count = ceil((snap(S) −
// lower) / step), capped. Falls back to `cap` when vol/T are not valid.
//
// Relationship to touch prob: under zero-drift BS, touch_prob ≈ 2 × vanilla_delta
// so a 2Δ outer boundary is ≈ 4% touch probability. Pure.
export function rungCountForDelta(
  somSpot: number,
  step: number,
  atmVol: number,
  T: number,
  deltaBound: number,
  cap = 25,
): number {
  if (!Number.isFinite(somSpot) || somSpot <= 0) return cap;
  if (!Number.isFinite(step) || step <= 0) return cap;
  if (!Number.isFinite(atmVol) || atmVol <= 0) return cap;
  if (!Number.isFinite(T) || T <= 0) return cap;
  if (deltaBound <= 0 || deltaBound >= 0.5) return cap;
  const sqrtT = Math.sqrt(T);
  const halfVarT = (atmVol * atmVol * T) / 2;
  const z = normInv(deltaBound); // negative for deltaBound < 0.5
  const lowerBound = somSpot * Math.exp(z * atmVol * sqrtT + halfVarT);
  const snap = Math.round(somSpot / step) * step;
  const raw = Math.ceil((snap - lowerBound) / step);
  return Math.min(cap, Math.max(1, raw));
}

// Years from now to an engine expiry ISO string. Uses the same local-wall-clock
// parse as parseEngineExpiry. Returns 0 on unparseable input or a past expiry.
export function yearsToExpiry(iso: string, now: Date = new Date()): number {
  const d = parseEngineExpiry(iso);
  if (!d) return 0;
  return Math.max(0, (d.getTime() - now.getTime()) / (365.25 * 24 * 3600 * 1000));
}

// Fixed fractional-digit count for every price a market renders, keyed off
// spot magnitude: < 10 -> 4dp (XRP, TRX), < 1000 -> 2dp (SOL, AVAX),
// >= 1000 -> 0dp (ETH, BTC). Covers both rung levels and OHLC/Latest
// tiles — a step-derived count (decimalsForStep) returns 0 for SOL because
// its step (10) is an integer, truncating tile prices like 147.32 to 147.
// Falls back to 2dp on an invalid spot. Pure.
export function decimalsForPrice(spot: number): number {
  if (!Number.isFinite(spot) || spot <= 0) return 2;
  if (spot < 10) return 4;
  if (spot < 1000) return 2;
  return 0;
}

// Direction of a single level relative to spot.
export function directionFor(level: number, spot: number): TouchDirection {
  return level >= spot ? 'upper' : 'lower';
}

// Build the rung set from an explicit list of levels. Levels are
// de-duplicated, the spot level (if present) is dropped, and the result is
// sorted DESCENDING (top of the card = highest level, matching the mock).
// Each rung's direction is assigned relative to spot.
export function buildRungs(spot: number, levels: number[]): Rung[] {
  const seen = new Set<number>();
  const rungs: Rung[] = [];
  for (const level of levels) {
    if (!Number.isFinite(level) || level <= 0) continue;
    if (level === spot) continue;
    if (seen.has(level)) continue;
    seen.add(level);
    rungs.push({ level, direction: directionFor(level, spot) });
  }
  rungs.sort((a, b) => b.level - a.level);
  return rungs;
}

// Magnitude-aware ABSOLUTE rung step. The multi-market board centres each
// card on its own live spot across three orders of magnitude (BTC ~104k,
// ETH ~3.9k, SOL ~210, HYPE ~38), so a single %-step would give visually
// uneven ladders. Instead we derive the step from spot's order of
// magnitude and snap it to a "nice" 1/2/5 x 10^n increment, targeting a
// step that is ~5% of spot (10 rungs straddle +/-50% of spot, the same
// span the old 5%/5-rung grid covered). Examples:
//   104000 -> 5000   3900 -> 200   210 -> 10   38 -> 2
// (HYPE's 2 is below the spec's ~5 but keeps the +/-50% span honest at
// that magnitude; the board reads the step, not a hard-coded tick.)
export function stepFor(spot: number): number {
  if (!Number.isFinite(spot) || spot <= 0) return 1;
  const target = spot * 0.05; // ~5% of spot per rung
  const mag = Math.pow(10, Math.floor(Math.log10(target)));
  const norm = target / mag; // in [1, 10)
  // Snap the leading digit to 1 / 2 / 5 -- the readable increments.
  const nice = norm < 1.5 ? 1 : norm < 3.5 ? 2 : norm < 7.5 ? 5 : 10;
  return nice * mag;
}

// Build a symmetric ladder around an anchor using a FIXED absolute step,
// WITHOUT magnitude snapping. Used for the start-of-month BTC ladder where
// the step is Kalshi's venue increment (BTC_STEP = 2500), not a derived one.
// The anchor is snapped to the nearest step-multiple so rungs read as round
// numbers on the venue grid; `count` rungs each side straddle the anchor.
// The on-anchor level is never emitted (buildRungs drops it -- spot gets its
// own marker row). Pure: no recentre, no live spot -- the month's levels are
// fixed once from start_of_month_spot.
export function gridLevelsFixed(anchor: number, step: number, count = 5): number[] {
  if (!Number.isFinite(anchor) || anchor <= 0) return [];
  if (!Number.isFinite(step) || step <= 0) return [];
  const base = Math.round(anchor / step) * step;
  const out: number[] = [];
  for (let k = count; k >= 1; k--) out.push(base + step * k);
  for (let k = 1; k <= count; k++) out.push(base - step * k);
  return out;
}

// Rungs-per-side for a band-derived ladder depth. The fixed 5-rung count gave
// a shallow ladder that never reached the deep tail (BTC bottomed ~$60k off a
// 72k open); William wants the ladder to span a BAND around the start-of-month
// anchor instead. We size the count so the rungs reach roughly +/- `pct` of the
// anchor (default 30%): countPerSide = ceil(pct * somSpot / step). To bound the
// pricing fan-out (each rung is two generic_pricer calls), the count is CAPPED
// at `cap` per side (default 12) and floored at 1. For BTC (72k anchor, 2500
// step) that's ceil(0.30*72000/2500) = ceil(8.64) = 9 rungs down -> ~$50k.
// Other markets get a comparable % span off their own magnitude-derived step
// (a market whose step is ~5% of anchor lands at ceil(0.30/0.05) = 6 per side).
// Pure: no React, no WS -- unit-tested in ladder.test.ts.
export function rungCountForBand(somSpot: number, step: number, pct = 0.3, cap = 12): number {
  if (!Number.isFinite(somSpot) || somSpot <= 0) return 1;
  if (!Number.isFinite(step) || step <= 0) return 1;
  const raw = Math.ceil((pct * somSpot) / step);
  return Math.min(cap, Math.max(1, raw));
}

// HIT flag for a single rung against the MTD realized extremes. An UPPER rung
// (level above the start-of-month spot) is HIT once the month-to-date HIGH
// reaches it (mtdHigh >= level); a LOWER rung is HIT once the month-to-date
// LOW reaches it (mtdLow <= level). A HIT rung has already been touched, so
// its touch probability is 1 by definition (overrides the live model prob).
// Returns false when the extremes are not yet known (null / non-finite).
export function rungHit(rung: Rung, mtdHigh: number | null, mtdLow: number | null): boolean {
  if (rung.direction === 'upper') {
    return mtdHigh !== null && Number.isFinite(mtdHigh) && mtdHigh >= rung.level;
  }
  return mtdLow !== null && Number.isFinite(mtdLow) && mtdLow <= rung.level;
}

// Signed % distance of a rung level from live spot: (level - spot) / spot * 100.
// Positive when the level is ABOVE spot (an upper rung not yet reached),
// negative below. Returns null when spot is unknown or non-positive, so the
// card can render an em-dash. Pure; unit-tested in ladder.test.ts.
export function pctFromSpot(level: number, spot: number | null): number | null {
  if (spot === null || !Number.isFinite(spot) || spot <= 0) return null;
  if (!Number.isFinite(level)) return null;
  return ((level - spot) / spot) * 100;
}

// Colour bucket for a touch probability in [0, 1]. Mirrors the mock's
// teal -> yellow -> orange -> red ramp. Returned as a semantic key the
// card maps to a concrete colour token.
export type ProbBand = 'low' | 'mid' | 'high' | 'extreme';

export function probBand(p: number): ProbBand {
  if (!Number.isFinite(p)) return 'low';
  if (p >= 0.3) return 'extreme';
  if (p >= 0.25) return 'high';
  if (p >= 0.12) return 'mid';
  return 'low';
}

// Build the OneTouch instrument seed for a single rung. payType, payout,
// payout_token and notional are pinned to the touch-probability reading:
//   one_touch, at_touch, payout 1.0, payout_token 'quote', notional 1
// so the engine PV reads directly as a risk-neutral probability in [0, 1].
// model is caller-supplied (bs for slice 1).
export function rungInstrument(
  id: string,
  token: string,
  rung: Rung,
  expiry: string,
  model: ModelKind,
): Instrument {
  const payType: PayType = 'at_touch';
  const base = {
    id,
    token,
    expiry,
    notional: 1,
    notionalToken: 'quote' as const,
    payType,
    model,
  };
  return rung.direction === 'upper'
    ? ({ ...base, contract: 'OneTouchUpper', upper: rung.level } as Instrument)
    : ({ ...base, contract: 'OneTouchLower', lower: rung.level } as Instrument);
}

// "Time since last update" for the card footer's freshness readout. Formats an
// elapsed millisecond span as "2m 14s" (>=1min) or "14s" (<1min). Sub-second
// and negative spans floor to "0s" (a clock that ticks coarsely can momentarily
// read `now` a touch behind the stamp). Pure -- unit-tested in ladder.test.ts.
export function fmtElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0s';
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

// Freshness band for the footer status dot, by elapsed age:
//   fresh   < 60s    (green)
//   stale   60-180s  (amber)
//   expired > 180s   (red)
// Drives the dot colour only; the elapsed label always shows. Boundaries are
// inclusive-low (60000 -> stale, 180000 -> stale) so a tick landing exactly on
// the edge reads the calmer band. Pure -- unit-tested in ladder.test.ts.
export type FreshnessBand = 'fresh' | 'stale' | 'expired';

export function freshnessBand(elapsedMs: number): FreshnessBand {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 60000) return 'fresh';
  if (elapsedMs <= 180000) return 'stale';
  return 'expired';
}
